import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Card,
  CardContent,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
  Chip
} from '@mui/material';
import {
  Email as EmailIcon,
  Upload as UploadIcon,
  Notifications as NotificationsIcon,
  Folder as FolderIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { format, parseISO, startOfDay, subDays, eachDayOfInterval, isWithinInterval } from 'date-fns';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

export default function ActivityTimeline({ activities, vendorName }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState(null);
  const [page, setPage] = useState(0);
  const ITEMS_PER_PAGE = 10;

  const handleOpenDrawer = (type) => {
    setDrawerType(type);
    setPage(0);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setDrawerType(null);
    setPage(0);
  };

  // Calculate statistics and time series data
  const { stats, chartData, dateRange } = useMemo(() => {
    if (!activities || activities.length === 0) {
      return { stats: { requests: 0, reminders: 0, certificateUploads: 0, otherDocuments: 0 }, chartData: [], dateRange: null };
    }

    // Filter relevant activities
    const relevantActivities = activities.filter(activity =>
      ['EMAIL_SEND', 'REQUEST_CERTIFICATE', 'REQUEST_DOCUMENT', 'CERTIFICATE_CREATION', 'DOCUMENT_CREATION'].includes(activity.action)
    );

    if (relevantActivities.length === 0) {
      return { stats: { requests: 0, reminders: 0, certificateUploads: 0, otherDocuments: 0 }, chartData: [], dateRange: null };
    }

    // Calculate stats
    const counts = {
      // REQUEST_CERTIFICATE + REQUEST_DOCUMENT = user-initiated requests
      requests: relevantActivities.filter(a => a.action === 'REQUEST_CERTIFICATE' || a.action === 'REQUEST_DOCUMENT').length,
      // EMAIL_SEND = automated reminder emails
      reminders: relevantActivities.filter(a => a.action === 'EMAIL_SEND').length,
      // CERTIFICATE_CREATION = certificate uploads by vendor
      certificateUploads: relevantActivities.filter(a => a.action === 'CERTIFICATE_CREATION').length,
      // DOCUMENT_CREATION = other document uploads by vendor
      otherDocuments: relevantActivities.filter(a => a.action === 'DOCUMENT_CREATION').length
    };

    // Get date range (last 30 days or from first activity)
    const now = new Date();
    const activityDates = relevantActivities.map(a => parseISO(a.createdAt));
    const oldestActivity = new Date(Math.min(...activityDates));
    const startDate = subDays(now, 30);
    const rangeStart = oldestActivity < startDate ? oldestActivity : startDate;

    // Create array of all days in range
    const allDays = eachDayOfInterval({ start: rangeStart, end: now });

    // Group activities by day
    const activityByDay = {};
    allDays.forEach(day => {
      const dayKey = format(startOfDay(day), 'yyyy-MM-dd');
      activityByDay[dayKey] = {
        date: dayKey,
        requests: 0,
        reminders: 0,
        certificateUploads: 0,
        otherDocuments: 0
      };
    });

    // Count activities per day
    relevantActivities.forEach(activity => {
      const activityDate = startOfDay(parseISO(activity.createdAt));
      const dayKey = format(activityDate, 'yyyy-MM-dd');

      if (activityByDay[dayKey]) {
        if (activity.action === 'REQUEST_CERTIFICATE' || activity.action === 'REQUEST_DOCUMENT') {
          activityByDay[dayKey].requests += 1;
        } else if (activity.action === 'EMAIL_SEND') {
          activityByDay[dayKey].reminders += 1;
        } else if (activity.action === 'CERTIFICATE_CREATION') {
          activityByDay[dayKey].certificateUploads += 1;
        } else if (activity.action === 'DOCUMENT_CREATION') {
          activityByDay[dayKey].otherDocuments += 1;
        }
      }
    });

    // Convert to array and format for chart
    const chartArray = Object.values(activityByDay).map(day => ({
      date: day.date,
      displayDate: format(parseISO(day.date), 'MMM dd'),
      Requests: day.requests,
      Reminders: day.reminders,
      'Certificate Uploads': day.certificateUploads,
      'Other Documents': day.otherDocuments
    }));

    return {
      stats: counts,
      chartData: chartArray,
      dateRange: {
        start: format(rangeStart, 'MMM dd'),
        end: format(now, 'MMM dd, yyyy')
      }
    };
  }, [activities]);

  // Filter activities for drawer based on type
  const filteredActivities = useMemo(() => {
    if (!drawerType || !activities) return [];

    const actionMap = {
      'requests': ['REQUEST_CERTIFICATE', 'REQUEST_DOCUMENT'],
      'reminders': ['EMAIL_SEND'],
      'certificateUploads': ['CERTIFICATE_CREATION'],
      'otherDocuments': ['DOCUMENT_CREATION']
    };

    const relevantActions = actionMap[drawerType] || [];
    return activities
      .filter(activity => relevantActions.includes(activity.action))
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [activities, drawerType]);

  const paginatedActivities = useMemo(() => {
    const start = page * ITEMS_PER_PAGE;
    return filteredActivities.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredActivities, page]);

  const getDrawerTitle = () => {
    const titles = {
      'requests': 'Requests',
      'reminders': 'Reminders',
      'certificateUploads': 'Certificate Uploads',
      'otherDocuments': 'Other Documents'
    };
    return titles[drawerType] || 'Activities';
  };

  const getActivityIcon = () => {
    const icons = {
      'requests': <EmailIcon />,
      'reminders': <NotificationsIcon />,
      'certificateUploads': <UploadIcon />,
      'otherDocuments': <FolderIcon />
    };
    return icons[drawerType];
  };

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const date = payload[0]?.payload?.displayDate;
      return (
        <Paper sx={{ p: 1.5, border: '1px solid #e0e0e0' }} elevation={3}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {date}
          </Typography>
          {payload.map((entry, index) => (
            <Typography
              key={`item-${index}`}
              variant="body2"
              sx={{ color: entry.color, fontSize: '0.875rem' }}
            >
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  if (!activities || activities.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Activity Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No activity recorded yet for {vendorName}.
        </Typography>
      </Paper>
    );
  }

  if (chartData.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Activity Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No email, request, or upload activity to display for {vendorName}.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6">
          {vendorName} Activity Dashboard
        </Typography>
        {dateRange && (
          <Typography variant="body2" color="text.secondary">
            {dateRange.start} - {dateRange.end}
          </Typography>
        )}
      </Stack>

      {/* Statistics Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Card variant="outlined" sx={{ flex: 1, bgcolor: '#e3f2fd', cursor: 'pointer' }} onClick={() => handleOpenDrawer('requests')}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <EmailIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                <Box>
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                    {stats.requests}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Requests
                  </Typography>
                </Box>
              </Stack>
              <IconButton size="small" sx={{ color: '#1976d2' }}>
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1, bgcolor: '#fff4e5', cursor: 'pointer' }} onClick={() => handleOpenDrawer('reminders')}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <NotificationsIcon sx={{ color: '#ed6c02', fontSize: 20 }} />
                <Box>
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                    {stats.reminders}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Reminders
                  </Typography>
                </Box>
              </Stack>
              <IconButton size="small" sx={{ color: '#ed6c02' }}>
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1, bgcolor: '#e8f5e9', cursor: 'pointer' }} onClick={() => handleOpenDrawer('certificateUploads')}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <UploadIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                <Box>
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                    {stats.certificateUploads}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Certificate Uploads
                  </Typography>
                </Box>
              </Stack>
              <IconButton size="small" sx={{ color: '#2e7d32' }}>
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1, bgcolor: '#f3e5f5', cursor: 'pointer' }} onClick={() => handleOpenDrawer('otherDocuments')}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <FolderIcon sx={{ color: '#9c27b0', fontSize: 20 }} />
                <Box>
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                    {stats.otherDocuments}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Other Documents
                  </Typography>
                </Box>
              </Stack>
              <IconButton size="small" sx={{ color: '#9c27b0' }}>
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Interactive Chart */}
      <Box sx={{ width: '100%', height: 300, mt: 2 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="displayDate"
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
              axisLine={{ stroke: '#e0e0e0' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
              axisLine={{ stroke: '#e0e0e0' }}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="line"
              iconSize={20}
            />
            <Line
              type="monotone"
              dataKey="Requests"
              stroke="#1976d2"
              strokeWidth={2}
              dot={{ fill: '#1976d2', r: 4 }}
              activeDot={{ r: 6 }}
              name="Requests"
            />
            <Line
              type="monotone"
              dataKey="Reminders"
              stroke="#ed6c02"
              strokeWidth={2}
              dot={{ fill: '#ed6c02', r: 4 }}
              activeDot={{ r: 6 }}
              name="Reminders"
            />
            <Line
              type="monotone"
              dataKey="Certificate Uploads"
              stroke="#2e7d32"
              strokeWidth={2}
              dot={{ fill: '#2e7d32', r: 4 }}
              activeDot={{ r: 6 }}
              name="Certificate Uploads"
            />
            <Line
              type="monotone"
              dataKey="Other Documents"
              stroke="#9c27b0"
              strokeWidth={2}
              dot={{ fill: '#9c27b0', r: 4 }}
              activeDot={{ r: 6 }}
              name="Other Documents"
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {/* Activity Details Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 500 } }
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Drawer Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              {getActivityIcon()}
              <Typography variant="h6">
                {getDrawerTitle()}
              </Typography>
            </Stack>
            <IconButton onClick={handleCloseDrawer}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {/* Activity List */}
          {filteredActivities.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No activities found
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Showing {page * ITEMS_PER_PAGE + 1}-{Math.min((page + 1) * ITEMS_PER_PAGE, filteredActivities.length)} of {filteredActivities.length}
              </Typography>

              <List>
                {paginatedActivities.map((activity, index) => (
                  <React.Fragment key={activity.id || index}>
                    <ListItem sx={{ px: 0, flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Stack direction="row" justifyContent="space-between" sx={{ width: '100%', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight="600">
                          {activity.userFullName || 'Unknown User'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(parseISO(activity.createdAt), 'MMM dd, yyyy h:mm a')}
                        </Typography>
                      </Stack>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {activity.action === 'EMAIL_SEND' && 'Reminder email sent'}
                        {activity.action === 'REQUEST_CERTIFICATE' && 'Certificate requested'}
                        {activity.action === 'REQUEST_DOCUMENT' && 'Document requested'}
                        {activity.action === 'CERTIFICATE_CREATION' && 'Certificate uploaded'}
                        {activity.action === 'DOCUMENT_CREATION' && 'Document uploaded'}
                      </Typography>

                      {activity.field && (() => {
                        try {
                          const fieldData = JSON.parse(activity.field);
                          return (
                            <Box sx={{ width: '100%' }}>
                              {fieldData.message && (
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  {fieldData.message}
                                </Typography>
                              )}
                              {fieldData.details && (
                                <Box sx={{ pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                                  {fieldData.details.categories && (
                                    <Typography variant="caption" color="text.secondary">
                                      Categories: {fieldData.details.categories.join(', ')}
                                    </Typography>
                                  )}
                                  {fieldData.details.fileCount && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Files: {fieldData.details.fileCount}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>
                          );
                        } catch (e) {
                          return null;
                        }
                      })()}
                    </ListItem>
                    {index < paginatedActivities.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>

              {/* Pagination Controls */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                <Button
                  size="small"
                  disabled={page === 0}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Page {page + 1} of {Math.ceil(filteredActivities.length / ITEMS_PER_PAGE)}
                </Typography>
                <Button
                  size="small"
                  disabled={(page + 1) * ITEMS_PER_PAGE >= filteredActivities.length}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </Drawer>
    </Paper>
  );
}
